import { store } from './state';

type RouteHandler = () => void;

interface Route {
  path: string;
  handler: RouteHandler;
  requiresAuth: boolean;
}

const routes: Route[] = [];

export function addRoute(path: string, handler: RouteHandler, requiresAuth = true): void {
  routes.push({ path, handler, requiresAuth });
}

export function navigate(path: string): void {
  window.location.hash = `#${path}`;
}

export function getCurrentPath(): string {
  return window.location.hash.slice(1) || '/';
}

function matchRoute(path: string): Route | null {
  // Exact match first
  let route = routes.find(r => r.path === path);
  if (route) return route;

  // Pattern match (e.g., /chat/:id)
  for (const r of routes) {
    if (r.path.includes(':')) {
      const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return r;
      }
    }
  }

  return null;
}

export function extractParams(routePath: string, actualPath: string): Record<string, string> {
  const params: Record<string, string> = {};
  const routeParts = routePath.split('/');
  const actualParts = actualPath.split('/');

  for (let i = 0; i < routeParts.length; i++) {
    if (routeParts[i].startsWith(':')) {
      const paramName = routeParts[i].slice(1);
      params[paramName] = actualParts[i];
    }
  }

  return params;
}

export function getRouteParams(): Record<string, string> {
  const path = getCurrentPath();

  // Find matching route with params
  for (const r of routes) {
    if (r.path.includes(':')) {
      const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return extractParams(r.path, path);
      }
    }
  }

  return {};
}

export function handleRoute(): void {
  const path = getCurrentPath();
  const route = matchRoute(path);

  if (!route) {
    // Default to browse if authenticated, welcome otherwise
    if (store.isAuthenticated()) {
      navigate('/browse');
    } else {
      navigate('/welcome');
    }
    return;
  }

  if (route.requiresAuth && !store.isAuthenticated()) {
    navigate('/welcome');
    return;
  }

  // Redirect authenticated users away from login page (but allow welcome/home)
  if (!route.requiresAuth && route.path === '/login' && store.isAuthenticated()) {
    navigate('/browse');
    return;
  }

  store.setCurrentView(route.path);
  route.handler();
}

export function initRouter(): void {
  window.addEventListener('hashchange', handleRoute);
  handleRoute();
}

export default {
  addRoute,
  navigate,
  getCurrentPath,
  extractParams,
  getRouteParams,
  handleRoute,
  initRouter,
};
